import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import type { Broadcast, BroadcastFilters } from './types';
import { EMPTY_BROADCAST } from './types';
import { BroadcastComposer } from './components/BroadcastComposer';
import { AudienceFilter } from './components/AudienceFilter';
import { BroadcastHistory } from './components/BroadcastHistory';
import { BroadcastDetailModal } from './components/BroadcastDetailModal';
import { htmlToPlainText } from './components/RichContentRenderer';

type Tab = 'compose' | 'history';

const AdminComunicadosPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('compose');
  const [draft, setDraft] = useState<Partial<Broadcast>>({ ...EMPTY_BROADCAST });
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendMode, setSendMode] = useState<'now' | 'schedule'>('now');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [detailBroadcast, setDetailBroadcast] = useState<Broadcast | null>(null);

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchBroadcasts = useCallback(async () => {
    const { data } = await supabase.from('broadcasts').select('*').order('created_at', { ascending: false }).limit(200);
    setBroadcasts((data as Broadcast[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchBroadcasts(); }, [fetchBroadcasts]);

  const patchDraft = (patch: Partial<Broadcast>) => setDraft((prev) => ({ ...prev, ...patch }));

  const resolveAudience = async (filters: BroadcastFilters): Promise<string[]> => {
    try {
      if (filters.audience === 'all') {
        const { data } = await supabase.from('profiles').select('id');
        return (data ?? []).map((p: { id: string }) => p.id);
      }
      if (filters.audience === 'plan' && filters.plan_id) {
        const { data } = await supabase.from('subscriptions').select('user_id').eq('plan_id', filters.plan_id);
        return (data ?? []).map((s: { user_id: string }) => s.user_id);
      }
      if (filters.audience === 'status' && filters.subscription_status) {
        const statusMap: Record<string, string> = { active: 'active', trial: 'trialing', overdue: 'past_due', cancelled: 'cancelled' };
        const { data } = await supabase.from('subscriptions').select('user_id').eq('status', statusMap[filters.subscription_status] ?? filters.subscription_status);
        return (data ?? []).map((s: { user_id: string }) => s.user_id);
      }
      if (filters.audience === 'inactive') {
        const days = filters.inactive_days ?? 30;
        const threshold = new Date(Date.now() - days * 86400_000).toISOString();
        const { data } = await supabase.from('profiles').select('id').lt('updated_at', threshold);
        return (data ?? []).map((p: { id: string }) => p.id);
      }
      if (filters.audience === 'no_onboarding') {
        const { data } = await supabase.from('profiles').select('id').eq('onboarding_completed', false);
        return (data ?? []).map((p: { id: string }) => p.id);
      }
      if (filters.audience === 'with_goals') {
        const { data } = await supabase.from('goals').select('user_id');
        return [...new Set((data ?? []).map((g: { user_id: string }) => g.user_id))];
      }
      if (filters.audience === 'without_goals') {
        const { data: goals } = await supabase.from('goals').select('user_id');
        const withGoals = new Set((goals ?? []).map((g: { user_id: string }) => g.user_id));
        const { data: profiles } = await supabase.from('profiles').select('id');
        return (profiles ?? []).map((p: { id: string }) => p.id).filter((id: string) => !withGoals.has(id));
      }
      if (filters.audience === 'with_transactions') {
        const { data } = await supabase.from('transactions').select('user_id');
        return [...new Set((data ?? []).map((t: { user_id: string }) => t.user_id))];
      }
      if (filters.audience === 'without_transactions') {
        const { data: txns } = await supabase.from('transactions').select('user_id');
        const withTxns = new Set((txns ?? []).map((t: { user_id: string }) => t.user_id));
        const { data: profiles } = await supabase.from('profiles').select('id');
        return (profiles ?? []).map((p: { id: string }) => p.id).filter((id: string) => !withTxns.has(id));
      }
      const { data } = await supabase.from('profiles').select('id');
      return (data ?? []).map((p: { id: string }) => p.id);
    } catch {
      return [];
    }
  };

  const handleSend = async () => {
    const contentText = htmlToPlainText(draft.content ?? '');
    if (!draft.title?.trim() || !contentText.trim()) {
      showToast('error', 'Preencha o título e o conteúdo antes de enviar.');
      return;
    }
    if ((draft.channels ?? []).length === 0) {
      showToast('error', 'Selecione ao menos um canal de envio.');
      return;
    }
    if (sendMode === 'schedule' && (!scheduleDate || !scheduleTime)) {
      showToast('error', 'Defina a data e hora de agendamento.');
      return;
    }

    setSending(true);
    try {
      const isScheduled = sendMode === 'schedule';
      const scheduledAt = isScheduled ? new Date(`${scheduleDate}T${scheduleTime}:00`).toISOString() : null;

      // Resolve audience
      const recipientIds = await resolveAudience(draft.filters ?? { audience: 'all' });

      // Insert broadcast
      const { data: inserted, error } = await supabase.from('broadcasts').insert({
        title: draft.title,
        subtitle: draft.subtitle ?? null,
        content: draft.content,
        image_url: draft.image_url ?? null,
        cta_label: draft.cta_label ?? null,
        cta_url: draft.cta_url ?? null,
        channels: draft.channels,
        filters: draft.filters ?? { audience: 'all' },
        status: isScheduled ? 'scheduled' : 'sending',
        scheduled_at: scheduledAt,
        created_by: user?.id ?? null,
        total_recipients: recipientIds.length,
        sent_count: 0,
        failed_count: 0,
      }).select().maybeSingle();

      if (error || !inserted) throw new Error(error?.message ?? 'Erro ao criar comunicado');

      if (!isScheduled) {
        const bcastId = (inserted as Broadcast).id;
        let sentCount = 0;
        let failedCount = 0;

        // Insert broadcast_recipients
        if (recipientIds.length > 0) {
          const recipientRows = recipientIds.map((uid: string) => ({
            broadcast_id: bcastId,
            user_id: uid,
            notification_sent: (draft.channels ?? []).includes('internal'),
            email_sent: (draft.channels ?? []).includes('email'),
          }));
          // Batch insert in chunks of 500
          for (let i = 0; i < recipientRows.length; i += 500) {
            const { error: recipError } = await supabase
              .from('broadcast_recipients')
              .insert(recipientRows.slice(i, i + 500));
            if (recipError) {
              console.error('broadcast_recipients insert error:', recipError.message);
            }
          }

          // If internal channel, insert notifications for each user
          if ((draft.channels ?? []).includes('internal')) {
            const plainBody = htmlToPlainText(draft.content ?? '').slice(0, 160);
            const notifRows = recipientIds.map((uid: string) => ({
              user_id: uid,
              type: 'broadcast',
              title: draft.title!,
              body: draft.subtitle ?? plainBody ?? null,
              data: {
                broadcast_id: bcastId,
                content: draft.content,
                image_url: draft.image_url ?? null,
                cta_label: draft.cta_label ?? null,
                cta_url: draft.cta_url ?? null,
              },
            }));
            // Insert in chunks and track real success count
            for (let i = 0; i < notifRows.length; i += 500) {
              const chunk = notifRows.slice(i, i + 500);
              const { data: insertedNotifs, error: notifError } = await supabase
                .from('notifications')
                .insert(chunk)
                .select('id');
              if (notifError) {
                console.error('notifications insert error:', notifError.message);
                failedCount += chunk.length;
              } else {
                sentCount += (insertedNotifs ?? []).length;
                failedCount += chunk.length - (insertedNotifs ?? []).length;
              }
            }
          } else {
            // email-only channel: count all recipients as "sent" (email queue)
            sentCount = recipientIds.length;
          }
        }

        // Mark as sent with real counts
        await supabase.from('broadcasts').update({
          status: 'sent',
          sent_at: new Date().toISOString(),
          sent_count: sentCount,
          failed_count: failedCount,
          read_count: 0,
        }).eq('id', bcastId);

        if (failedCount > 0 && sentCount === 0) {
          showToast('error', `Erro ao entregar comunicado. Verifique as permissões do sistema.`);
        } else if (failedCount > 0) {
          showToast('success', `Comunicado enviado para ${sentCount.toLocaleString('pt-BR')} destinatários (${failedCount} falhas).`);
        } else {
          showToast('success', `Comunicado enviado para ${sentCount.toLocaleString('pt-BR')} destinatários!`);
        }
      } else {
        showToast('success', `Comunicado agendado para ${new Date(scheduledAt!).toLocaleString('pt-BR')}.`);
      }

      // Reset draft
      setDraft({ ...EMPTY_BROADCAST });
      setSendMode('now');
      setScheduleDate('');
      setScheduleTime('');
      fetchBroadcasts();
      setActiveTab('history');
    } catch (err: unknown) {
      showToast('error', err instanceof Error ? err.message : 'Erro inesperado ao enviar comunicado.');
    }
    setSending(false);
  };

  const handleSaveDraft = async () => {
    if (!draft.title?.trim()) { showToast('error', 'Informe um título para salvar o rascunho.'); return; }
    const { error } = await supabase.from('broadcasts').insert({
      title: draft.title,
      subtitle: draft.subtitle ?? null,
      content: draft.content ?? '',
      image_url: draft.image_url ?? null,
      cta_label: draft.cta_label ?? null,
      cta_url: draft.cta_url ?? null,
      channels: draft.channels ?? ['internal'],
      filters: draft.filters ?? { audience: 'all' },
      status: 'draft',
      scheduled_at: null,
      created_by: user?.id ?? null,
      total_recipients: 0,
      sent_count: 0,
      failed_count: 0,
    });
    if (error) { showToast('error', 'Erro ao salvar rascunho.'); return; }
    showToast('success', 'Rascunho salvo com sucesso!');
    setDraft({ ...EMPTY_BROADCAST });
    fetchBroadcasts();
  };

  const handleDuplicate = (b: Broadcast) => {
    setDraft({
      title: `Cópia de ${b.title}`,
      subtitle: b.subtitle,
      content: b.content,
      image_url: b.image_url,
      cta_label: b.cta_label,
      cta_url: b.cta_url,
      channels: b.channels,
      filters: b.filters,
      status: 'draft',
      scheduled_at: null,
    });
    setActiveTab('compose');
  };

  const handleResend = (b: Broadcast) => {
    setDraft({
      title: b.title,
      subtitle: b.subtitle,
      content: b.content,
      image_url: b.image_url,
      cta_label: b.cta_label,
      cta_url: b.cta_url,
      channels: b.channels,
      filters: b.filters,
      status: 'draft',
      scheduled_at: null,
    });
    setActiveTab('compose');
  };

  const handleCancel = async (id: string) => {
    await supabase.from('broadcasts').update({ status: 'cancelled' }).eq('id', id);
    fetchBroadcasts();
    showToast('success', 'Comunicado cancelado.');
  };

  const stats = {
    sent: broadcasts.filter((b) => b.status === 'sent').length,
    scheduled: broadcasts.filter((b) => b.status === 'scheduled').length,
    draft: broadcasts.filter((b) => b.status === 'draft').length,
    totalRecipients: broadcasts.reduce((acc, b) => acc + (b.total_recipients ?? 0), 0),
  };

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl border shadow-lg transition-all ${
          toast.type === 'success' ? 'bg-emerald-600/20 border-emerald-500/30 text-emerald-300' : 'bg-red-600/20 border-red-500/30 text-red-300'
        }`}>
          <i className={`${toast.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} text-[16px]`} />
          <span className="text-[13px] font-600">{toast.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-[24px] font-700 text-white">Comunicados</h1>
        <p className="text-white/40 text-[14px] mt-1">Crie, segmente e envie comunicados para seus clientes</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Enviados', value: stats.sent, icon: 'ri-send-plane-fill', color: 'bg-emerald-600' },
          { label: 'Agendados', value: stats.scheduled, icon: 'ri-calendar-schedule-line', color: 'bg-amber-600' },
          { label: 'Rascunhos', value: stats.draft, icon: 'ri-draft-line', color: 'bg-white/20' },
          { label: 'Total destinatários', value: stats.totalRecipients.toLocaleString('pt-BR'), icon: 'ri-group-line', color: 'bg-violet-600' },
        ].map((c) => (
          <div key={c.label} className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center gap-3">
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${c.color}`}>
              <i className={`${c.icon} text-white text-[18px]`} />
            </div>
            <div>
              <p className="text-[11px] text-white/30">{c.label}</p>
              <p className="text-[20px] font-700 text-white">{c.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {([
          { id: 'compose', label: 'Novo Comunicado', icon: 'ri-megaphone-line' },
          { id: 'history', label: 'Histórico', icon: 'ri-history-line', badge: broadcasts.length > 0 ? broadcasts.length : undefined },
        ] as const).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl border transition-all cursor-pointer whitespace-nowrap ${
              activeTab === t.id
                ? 'bg-violet-600/20 border-violet-500/40 text-white'
                : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10 hover:text-white'
            }`}
          >
            <i className={`${t.icon} text-[16px]`} />
            <span className="text-[13px] font-700">{t.label}</span>
            {'badge' in t && t.badge !== undefined && (
              <span className="px-1.5 py-0.5 rounded-full bg-violet-600/30 text-violet-300 text-[10px] font-700">{t.badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* COMPOSE TAB */}
      {activeTab === 'compose' && (
        <div className="grid grid-cols-[1fr_340px] gap-6">
          {/* Left: Composer */}
          <div className="space-y-6">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h2 className="text-[15px] font-700 text-white mb-5 flex items-center gap-2">
                <i className="ri-edit-2-line text-violet-400" />
                Editor do Comunicado
              </h2>
              <BroadcastComposer draft={draft} onChange={patchDraft} />
            </div>

            {/* Send options */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h2 className="text-[15px] font-700 text-white mb-4 flex items-center gap-2">
                <i className="ri-send-plane-line text-violet-400" />
                Envio
              </h2>

              <div className="flex gap-3 mb-4">
                {([
                  { id: 'now', label: 'Enviar agora', icon: 'ri-send-plane-fill' },
                  { id: 'schedule', label: 'Agendar envio', icon: 'ri-calendar-schedule-line' },
                ] as const).map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setSendMode(m.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all cursor-pointer whitespace-nowrap ${
                      sendMode === m.id
                        ? 'bg-violet-600/20 border-violet-500/40 text-white'
                        : 'bg-white/5 border-white/10 text-white/40 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <i className={`${m.icon} text-[15px]`} />
                    <span className="text-[13px] font-600">{m.label}</span>
                  </button>
                ))}
              </div>

              {sendMode === 'schedule' && (
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="text-[11px] text-white/30 font-600 uppercase tracking-wider mb-1 block">Data</label>
                    <input
                      type="date"
                      value={scheduleDate}
                      onChange={(e) => setScheduleDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] focus:outline-none focus:border-violet-500/60 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] text-white/30 font-600 uppercase tracking-wider mb-1 block">Horário</label>
                    <input
                      type="time"
                      value={scheduleTime}
                      onChange={(e) => setScheduleTime(e.target.value)}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] focus:outline-none focus:border-violet-500/60 transition-colors"
                    />
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={sending}
                  className="flex items-center gap-2 px-6 py-3 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl text-white text-[14px] font-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {sending ? (
                    <><i className="ri-loader-4-line animate-spin text-[15px]" /> Enviando...</>
                  ) : sendMode === 'schedule' ? (
                    <><i className="ri-calendar-schedule-line text-[15px]" /> Agendar Comunicado</>
                  ) : (
                    <><i className="ri-send-plane-fill text-[15px]" /> Enviar Agora</>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  disabled={sending}
                  className="flex items-center gap-2 px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white/50 hover:text-white text-[13px] font-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  <i className="ri-save-line text-[14px]" /> Salvar Rascunho
                </button>
              </div>
            </div>
          </div>

          {/* Right: Audience filter */}
          <div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
              <h2 className="text-[15px] font-700 text-white mb-4 flex items-center gap-2">
                <i className="ri-group-3-line text-violet-400" />
                Público-alvo
              </h2>
              <AudienceFilter
                filters={draft.filters ?? { audience: 'all' }}
                onChange={(f) => patchDraft({ filters: f })}
              />
            </div>
          </div>
        </div>
      )}

      {/* HISTORY TAB */}
      {activeTab === 'history' && (
        <div>
          {/* Filter bar */}
          <div className="flex items-center gap-2 mb-5 flex-wrap">
            <span className="text-[12px] text-white/30">Filtrar:</span>
            {(['all', 'sent', 'scheduled', 'draft', 'cancelled'] as const).map((s) => {
              const labels: Record<string, string> = { all: 'Todos', sent: 'Enviados', scheduled: 'Agendados', draft: 'Rascunhos', cancelled: 'Cancelados' };
              const count = s === 'all' ? broadcasts.length : broadcasts.filter((b) => b.status === s).length;
              return (
                <button key={s} type="button" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white/40 hover:bg-white/10 hover:text-white text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap">
                  {labels[s]} <span className="text-white/20">{count}</span>
                </button>
              );
            })}
          </div>
          <BroadcastHistory
            broadcasts={broadcasts}
            loading={loading}
            onView={setDetailBroadcast}
            onDuplicate={handleDuplicate}
            onResend={handleResend}
            onCancel={handleCancel}
          />
        </div>
      )}

      {/* Detail modal */}
      <BroadcastDetailModal broadcast={detailBroadcast} onClose={() => setDetailBroadcast(null)} />
    </div>
  );
};

export default AdminComunicadosPage;
